import type { ChainKey, NativeChainKey } from '@infinex/chain-config';
import { CHAIN_KEYS } from '@infinex/chain-config';

import type { AssetId, LstAssetMetadata, OfficialAsset } from './assets';
import { isValidChainAddress, makeChainAddressId } from './assets';
import type { ChainAsset } from './groups';
import { splitOnFirstUnderscore } from './utils';

export type TokenChainAsset = Omit<ChainAsset, 'type'> & {
  type: 'token';
};

export type NftChainAsset = Omit<ChainAsset, 'type'> & {
  type: 'nft';
};

export type OfficialLstChainAsset = {
  chain: ChainKey;
  address: string;
  assetId: AssetId;
  lstAssetMetadata: LstAssetMetadata;
};

export type ChainAssetId = `${ChainKey}_${AssetId}`; // Example: base_usd_coin
export type ChainAddressId = `${ChainKey}_${string}`; // Example: base_0x1817ccda45325767A1f17AFA4A1EdcFb091E77D4
export type ChainAddressIdObject = {
  chain: ChainKey; // Example: base
  address: string; // Example: 0x1817ccda45325767A1f17AFA4A1EdcFb091E77D4
};

/** Chain assets keyed by chain+address that are known to carry an assetId. */
export type ChainAssetsByChainAddressId = Record<
  ChainAddressId,
  ChainAsset & { assetId: AssetId }
>;

// TODO: should be Partial
export type NativeChainAssetsByChain = Record<
  NativeChainKey,
  ChainAsset & { assetId: AssetId }
>;

/**
 * creates a ChainAssetId (chain + groupId)
 */
export function makeChainAssetId(
  chain: ChainKey,
  assetId: AssetId
): ChainAssetId {
  return `${chain}_${assetId}`;
}

export function getChainAssetMap(
  chainAssets: (ChainAsset & { assetId: AssetId })[]
): Map<ChainAssetId, ChainAsset & { assetId: AssetId }> {
  return chainAssets.reduce((acc, ca) => {
    acc.set(makeChainAssetId(ca.chain, ca.assetId), ca);
    return acc;
  }, new Map());
}

export function getChainAsset(
  chainAssets: (ChainAsset & { assetId: AssetId })[],
  search:
    | { chainAssetId: ChainAssetId }
    | { assetId: AssetId; chain: ChainKey }
    | { address: string; chain: ChainKey }
): (ChainAsset & { assetId: AssetId }) | undefined {
  if ('chainAssetId' in search) {
    return chainAssets.find(
      (a) => makeChainAssetId(a.chain, a.assetId) === search.chainAssetId
    );
  } else if ('address' in search) {
    return chainAssets.find(
      (a) =>
        a.address.toString().toLowerCase() ===
          search.address.toString().toLowerCase() && a.chain === search.chain
    );
  } else {
    return chainAssets.find(
      (a) => a.assetId === search.assetId && a.chain === search.chain
    );
  }
}

/**
 * @example getChainFromChainAssetId('ethereum_usd_coin') === 'ethereum'
 */
export function getChainFromChainAssetId(chainAssetId: ChainAssetId): ChainKey {
  // this type assertion
  return splitOnFirstUnderscore(chainAssetId)[0] as ChainKey;
}

export type ChainAddressMap = Record<
  ChainAddressId,
  ChainAsset & { assetId: AssetId }
>;

export function getChainAddressMap(
  chainAssets: (ChainAsset & { assetId: AssetId })[]
): ChainAddressMap {
  return chainAssets.reduce((acc, ca) => {
    acc[makeChainAddressId(ca.chain, ca.address)] = ca;
    return acc;
  }, {} as ChainAddressMap);
}

export function getOfficialAssetsFromChainAssets(
  chainAssets: (ChainAsset & { assetId: AssetId })[],
  includePriceId = false
): OfficialAsset[] {
  const uniqueAssets = new Set(
    chainAssets.map(({ assetId, name, symbol, type, coingeckoId, imgUrl }) =>
      JSON.stringify({
        id: assetId,
        name,
        symbol,
        imgUrl,
        type,
        coingeckoId: includePriceId ? coingeckoId : undefined,
      })
    )
  );

  return Array.from(uniqueAssets).map((a) => JSON.parse(a)) as OfficialAsset[];
}

export function chainAssetToAsset(
  chainAsset: ChainAsset & { assetId: AssetId }
): OfficialAsset {
  const { assetId, name, symbol, type, coingeckoId, imgUrl } = chainAsset;

  return {
    id: assetId,
    name,
    symbol,
    imgUrl,
    type,
    coingeckoId,
  };
}

export function chainAddressIdToObject(
  chainAddressId: ChainAddressId
): ChainAddressIdObject {
  return {
    chain: splitOnFirstUnderscore(chainAddressId)[0] as ChainKey,
    address: splitOnFirstUnderscore(chainAddressId)[1],
  };
}

export function chainAssetIdToObject(chainAssetId: ChainAssetId): {
  chain: ChainKey;
  assetId: AssetId;
} {
  return {
    chain: splitOnFirstUnderscore(chainAssetId)[0] as ChainKey,
    assetId: splitOnFirstUnderscore(chainAssetId)[1] as AssetId,
  };
}

export const isValidChainAddressId = (chainAddressId: string): boolean => {
  const all = chainAddressId.split('_');
  const potentialChain = all[0] as ChainKey;
  const potentialAddress = all[1];

  if (
    CHAIN_KEYS.includes(potentialChain) &&
    isValidChainAddress(potentialAddress) &&
    all.length === 2
  ) {
    return true;
  }

  return false;
};

type AssetIdChainAssetFilter =
  | {
      chainAssetIds: ChainAssetId[];
    }
  | {
      assetIds: AssetId[];
    };

type AddressChainAssetFilter =
  | {
      chains: ChainKey[];
    }
  | {
      chainAddressIds: ChainAddressId[];
    };

export type ChainAssetFilter =
  | AssetIdChainAssetFilter
  | AddressChainAssetFilter;

export function filterChainAssets<
  C extends { chain: ChainKey; address: string; assetId: AssetId },
>(
  chainAssets: C[],
  filter: AssetIdChainAssetFilter,
  serializable?: true
): (C & { address: string })[];
export function filterChainAssets<
  C extends { chain: ChainKey; address: string; assetId: AssetId },
>(chainAssets: C[], filter: AssetIdChainAssetFilter, serializable?: false): C[];
export function filterChainAssets<
  C extends { chain: ChainKey; address: string },
>(
  chainAssets: C[],
  filter?: AddressChainAssetFilter,
  serializable?: true
): (C & { address: string })[];
export function filterChainAssets<
  C extends { chain: ChainKey; address: string },
>(
  chainAssets: C[],
  filter?: AddressChainAssetFilter,
  serializable?: false
): C[];
export function filterChainAssets<
  C extends { chain: ChainKey; address: string; assetId?: AssetId },
>(
  chainAssets: C[],
  filter?: ChainAssetFilter,
  serializable?: boolean
): C[] | (C & { address: string })[] {
  if (filter && Object.keys(filter).length > 1) {
    throw new Error('Only one filter condition at a time');
  }

  const filtered = chainAssets.flatMap((asset) => {
    const shouldInclude = !filter
      ? true
      : 'chains' in filter
        ? filter.chains.includes(asset.chain)
        : 'assetIds' in filter
          ? filter.assetIds.includes(asset.assetId as AssetId)
          : 'chainAssetIds' in filter
            ? filter.chainAssetIds.includes(
                makeChainAssetId(asset.chain, asset.assetId as AssetId)
              )
            : 'chainAddressIds' in filter
              ? filter.chainAddressIds.includes(
                  makeChainAddressId(asset.chain, asset.address)
                )
              : false;

    if (serializable) {
      return shouldInclude
        ? { ...asset, address: asset.address.toString() }
        : [];
    } else {
      return shouldInclude ? asset : [];
    }
  });

  return filtered as C[] | (C & { address: string })[];
}
